import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { CustomerHelper } from '../../util/customerHelper'
import PayApplyUI from './UI/PayApplyUI'
import {
  setCurrentPage,
  prepareForNextStep,
  initPayApplyWizard,
  getPaymentMethods,
  getCustomerInfo,
  selectPaymentMethodAndSetPaymentData,
  createApplicationOrPayment,
  updateApplication,
  updatePayment,
  skipSigning,
  eSign,
  paperSign,
  eIdentification,
  manualIdentification,
  preparePendingApplication,
  getCustomerTypes,
  paperSignCompleted,
  updatePurchaseAmountAndDebit,
  signDigitalAgreement,
  delayedPaymentCompleted,
} from '../../store/actions/pay-apply-actions'
import {
  PageModel,
  PathModel,
  isPath,
  pageByPath,
} from './models/private-models'
import { PayApplyType } from '../../models/payapply'
import { logout } from '../../store/actions/auth-actions'
import {
  createSteps,
  getStepForCurrentPage,
  handleApplicationResult,
  handlePaymentResult,
} from './Wizard/private-wizard-model'
import { useOnUpdate } from '../../util/utility-hooks'
import { sendNextPageEvent } from '../../util/ga-helpers'
import { SigningTypes } from '../../util/paymentModel'

const isApplication = payApply => {
  return payApply.current.type === PayApplyType.APPLICATION
}

const isPayment = payApply => {
  return payApply.current.type === PayApplyType.PAYMENT
}

const PayApply = props => {
  const [steps] = useState(createSteps())
  const [debounceNextClick, setDebounceNextClick] = useState(false)

  const setPageBasedOnPath = () => {
    if (
      props.payApply.wizard.currentPage &&
      !isPath(
        props.payApply.wizard.currentPage,
        props.history.location.pathname,
      )
    ) {
      setDebounceNextClick(false)
      props.setCurrentPage(pageByPath(props.history.location.pathname))
      props.prepareForNextStep(
        getStepForCurrentPage(pageByPath(props.history.location.pathname)),
      )
    }
  }

  const gotoPage = newPage => {
    if (props.payApply.wizard.currentPage === newPage) {
      return
    }
    props.setCurrentPage(newPage)
    props.prepareForNextStep(getStepForCurrentPage(newPage))

    if (PathModel[newPage]) {
      props.history.push(PathModel[newPage])
    }
  }

  const computeNextPage = () => {
    if (props.payApply.current.fatalError) {
      gotoPage(PageModel.ERROR)
      return
    }

    switch (props.payApply.wizard.currentPage) {
      case PageModel.GOVERNMENT_ID:
        if (
          props.payApply.paymentMethods &&
          (props.payApply.paymentMethods.length ||
            (!props.payApply.paymentMethods.length &&
              (props.payApply.pendingApplications.applications || []).length))
        ) {
          gotoPage(PageModel.PAYMENT_METHOD)
        }
        break
      case PageModel.PAYMENT_METHOD:
        if ((props.payApply.formSpec.formSchema || []).length) {
          gotoPage(PageModel.PAYMENT_METHOD_FORM_SPEC)
        } else if (
          props.payApply.pendingApplications.readyForSigning &&
          isApplication(props.payApply)
        ) {
          gotoPage(handleApplicationResult(props.payApply))
        } else if (isPayment(props.payApply)) {
          gotoPage(handlePaymentResult(props.payApply))
        }
        break
      case PageModel.PAYMENT_METHOD_FORM_SPEC:
      case PageModel.APPLICATION_SIGN:
      case PageModel.PAYMENT_SIGN:
      case PageModel.PAYMENT_IDENTIFICATION:
      case PageModel.APPLICATION_SUPPLEMENTING_REQUIRED:
      case PageModel.APPLICATION_INSPECTION:
      case PageModel.PAYMENT_INSPECTION:
      case PageModel.PAYMENT_ADJUST_PURCHASE_AMOUNT:
        if (isApplication(props.payApply)) {
          gotoPage(handleApplicationResult(props.payApply))
        } else if (isPayment(props.payApply)) {
          gotoPage(handlePaymentResult(props.payApply))
        }
        break
      default:
        break
    }
    setDebounceNextClick(false)
  }

  const PreInitPayApplyWizard = () => {
    if (
      props.history.location.pathname !== PathModel[PageModel.GOVERNMENT_ID]
    ) {
      gotoPage(PageModel.GOVERNMENT_ID)
    }
    props.initPayApplyWizard()
    props.getCustomerTypes()
  }

  const handlePageSubmit = data => {
    switch (props.payApply.wizard.currentPage) {
      case PageModel.GOVERNMENT_ID:
        props.getCustomerInfo(data.governmentId, data.customerType)
        break
      case PageModel.PAYMENT_METHOD:
        if (data.refreshPaymentMethods) {
          props
            .getPaymentMethods(
              {
                governmentId: CustomerHelper.fromPayApply(props.payApply)
                  .governmentId,
                customerType: props.payApply.wizard.wizard,
              },
              false,
              true,
            )
            .then(() => {
              setDebounceNextClick(false)
            })
          break
        } else if (data.pendingApplications) {
          props.preparePendingApplication(data.selectedPendingApplication)
          break
        }
        props.selectPaymentMethod(data)
        break
      case PageModel.PAYMENT_METHOD_FORM_SPEC:
        props.createApplicationOrPayment(data)
        break
      case PageModel.APPLICATION_SIGN:
      case PageModel.PAYMENT_SIGN:
        if (data.selectedSigningMethod === SigningTypes.PHYSICAL) {
          props.paperSignCompleted()
        } else if (
          data.selectedSigningMethod === SigningTypes.DIGITAL_AGREEMENT
        ) {
          return props.signDigitalAgreement(data)
        } else if (
          data.selectedSigningMethod === SigningTypes.ELECTRONIC &&
          data.isDelayPayment
        ) {
          props.delayedPaymentCompleted(data.automaticDebitDate)
        } else {
          props.eSign(props.payApply.current.id)
        }
        break
      case PageModel.PAYMENT_IDENTIFICATION:
        if (data.manualIdentification) {
          props.manualIdentification(props.payApply.current.id, {
            type: data.idType,
            refnr: data.refnr,
          })
        } else {
          props.eIdentification()
        }
        break
      case PageModel.APPLICATION_INSPECTION:
        props.updateApplication(props.payApply.current.id, false, true)
        break
      case PageModel.APPLICATION_SUPPLEMENTING_REQUIRED:
        props.updateApplication(props.payApply.current.id, false, true)
        break
      case PageModel.PAYMENT_INSPECTION:
        props.updatePayment(props.payApply.current.id, false, true)
        break
      case PageModel.PAYMENT_ADJUST_PURCHASE_AMOUNT:
        props.updatePurchaseAmountAndDebit()
        break
      case PageModel.APPLICATION_PUSHED_OUT_TO_CUSTOMER:
      case PageModel.PAYMENT_PUSHED_OUT_TO_CUSTOMER:
        break
      case PageModel.APPLICATION_REJECTED:
      case PageModel.PAYMENT_REJECTED:
      case PageModel.APPLICATION_DONE:
      case PageModel.PAYMENT_DONE:
      case PageModel.ERROR:
        props.logout()
        break
      default:
        break
    }
    return Promise.resolve()
  }

  const onNext = data => {
    const analyticsData = (data && data.analyticsData) || {}
    sendNextPageEvent({
      currentPage: props.payApply.wizard.currentPage,
      ...analyticsData,
    })
    if (!debounceNextClick) {
      setDebounceNextClick(true)
      return handlePageSubmit(data)
    }
    return Promise.resolve()
  }

  const onPrevious = () => {
    props.history.goBack()
  }

  useEffect(() => {
    setPageBasedOnPath()
  })

  useEffect(() => {
    if (
      props.history.location.pathname === PathModel[PageModel.GOVERNMENT_ID]
    ) {
      PreInitPayApplyWizard()
    }
  }, [])

  useOnUpdate(
    () => !props.isNewPageLoading && computeNextPage(),
    [props.newPageLoadedAt],
  )

  return (
    <PayApplyUI
      currentStep={props.payApply.wizard.currentStep}
      steps={steps}
      currentPage={props.payApply.wizard.currentPage}
      data={props.payApply}
      onNext={data => onNext(data)}
      onPrevious={() => onPrevious()}
      onCancel={() => PreInitPayApplyWizard()}
      history={props.history}
      logout={props.logout}
    />
  )
}

const mapStateToProps = state => {
  return {
    payApply: state.payApply,
    isNewPageLoading: state.payApply.wizard.newPageLoading,
    newPageLoadedAt: state.payApply.wizard.newPageLoadedAt,
  }
}

PayApply.propTypes = {
  prepareForNextStep: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  getCustomerInfo: PropTypes.func.isRequired,
  getPaymentMethods: PropTypes.func.isRequired,
  getCustomerTypes: PropTypes.func.isRequired,
  initPayApplyWizard: PropTypes.func.isRequired,
  selectPaymentMethod: PropTypes.func.isRequired,
  updateApplication: PropTypes.func.isRequired,
  updatePayment: PropTypes.func.isRequired,
  payApply: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
  isNewPageLoading: PropTypes.bool.isRequired,
  newPageLoadedAt: PropTypes.number.isRequired,
  createApplicationOrPayment: PropTypes.func.isRequired,
  eSign: PropTypes.func.isRequired,
  signDigitalAgreement: PropTypes.func.isRequired,
  delayedPaymentCompleted: PropTypes.func.isRequired,
  paperSignCompleted: PropTypes.func.isRequired,
  eIdentification: PropTypes.func.isRequired,
  manualIdentification: PropTypes.func.isRequired,
  preparePendingApplication: PropTypes.func.isRequired,
  updatePurchaseAmountAndDebit: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
}

const mapDispatchToProps = dispatch => ({
  initPayApplyWizard: () => {
    dispatch(initPayApplyWizard())
  },
  getCustomerTypes: () => {
    dispatch(getCustomerTypes())
  },
  getCustomerInfo: (governmentId, customerType) => {
    dispatch(getCustomerInfo(governmentId, customerType))
  },
  getPaymentMethods: (
    { governmentId, customerType },
    newPageLoading,
    onPageLoading,
  ) =>
    dispatch(
      getPaymentMethods(
        { governmentId, customerType },
        newPageLoading,
        onPageLoading,
      ),
    ),
  selectPaymentMethod: paymentMethod => {
    dispatch(selectPaymentMethodAndSetPaymentData(paymentMethod))
  },
  updateApplication: (applicationId, onPageLoad, newPageLoad) => {
    dispatch(updateApplication(applicationId, onPageLoad, newPageLoad))
  },
  updatePayment: (paymentId, onPageLoad, newPageLoad) => {
    dispatch(updatePayment(paymentId, onPageLoad, newPageLoad))
  },
  prepareForNextStep: stepIndex => {
    dispatch(prepareForNextStep(stepIndex))
  },
  setCurrentPage: pageIndex => {
    dispatch(setCurrentPage(pageIndex))
  },
  skipSigning: id => {
    dispatch(skipSigning(id))
  },
  eSign: () => {
    dispatch(eSign())
  },
  signDigitalAgreement: preferredCommunication =>
    dispatch(signDigitalAgreement(preferredCommunication)),
  paperSign: (paymentId, identificationType) => {
    dispatch(paperSign(paymentId, identificationType))
  },
  paperSignCompleted: () => {
    dispatch(paperSignCompleted())
  },
  delayedPaymentCompleted: automaticDebitDate => {
    dispatch(delayedPaymentCompleted(automaticDebitDate))
  },
  eIdentification: () => {
    dispatch(eIdentification())
  },
  manualIdentification: (paymentId, identificationType) => {
    dispatch(manualIdentification(paymentId, identificationType))
  },
  createApplicationOrPayment: formData => {
    dispatch(createApplicationOrPayment(formData))
  },
  preparePendingApplication: account => {
    dispatch(preparePendingApplication(account))
  },
  updatePurchaseAmountAndDebit: () => {
    dispatch(updatePurchaseAmountAndDebit)
  },
  logout: () => {
    dispatch(logout())
  },
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PayApply),
)
